/**
 * OverlapZones — MapLibre layer that renders polygons where two sites'
 * trade-area rings of the same radius intersect. Rendered as a red-
 * tinted fill + dashed stroke, on top of TradeAreaRings.
 *
 * Mirrors the imperative-MapLibre pattern used by TradeAreaRings:
 * the component takes the map instance + load flag and manages its
 * own GeoJSON source/layer pair; cleans them up on unmount.
 *
 * Inputs:
 *   - sites           — sites participating in the overlap calculation
 *   - radii           — ring radii to consider (default = TRADE_AREA_RINGS)
 *   - visible         — toggle to hide/show the layers without unmounting
 *
 * Linear: MYD-95 (I1). Replaces the inline overlap rendering in
 * `comparison/ComparisonMap.tsx` (F6) — F6 stays self-contained today,
 * but a closing-sweep PR can swap it to import this component.
 */

import { useEffect, useRef } from 'react'
import type maplibregl from 'maplibre-gl'
import type { Feature, FeatureCollection, Polygon } from 'geojson'

import { tradeAreaService } from '../services/tradeAreaService'
import { TRADE_AREA_RINGS, siteColorForIndex } from '../config/mapConfig'

const SOURCE_ID = 'overlap-zones-source'
const LAYER_ID_FILL = 'overlap-zones-fill-layer'
const LAYER_ID_STROKE = 'overlap-zones-stroke-layer'

const DEFAULT_FILL = '#ef4444'
const DEFAULT_FILL_OPACITY = 0.18
const DEFAULT_STROKE_OPACITY = 0.5
const DEFAULT_STROKE_WIDTH = 1.5

export interface SiteForOverlap {
	id: string
	latitude: number
	longitude: number
}

export interface OverlapZonesProps {
	map: maplibregl.Map | null
	isMapLoaded: boolean
	sites: SiteForOverlap[]
	visible: boolean
	radii?: number[]
	fillColor?: string
}

export function OverlapZones({
	map,
	isMapLoaded,
	sites,
	visible,
	radii,
	fillColor = DEFAULT_FILL,
}: OverlapZonesProps) {
	const initialized = useRef(false)

	useEffect(() => {
		if (!map || !isMapLoaded) return

		const radiiToUse = radii ?? (TRADE_AREA_RINGS as unknown as number[])
		const overlapFeatures: Feature<Polygon>[] = []

		if (visible) {
			// Build per-site rings, then compute pairwise overlaps. Site colour
			// is irrelevant for overlaps (we use a single shared red), but the
			// existing ring-generation API takes one — pass siteColorForIndex
			// for stability.
			const ringsBySite = sites.map((s, idx) =>
				tradeAreaService.generateRings(
					[s.longitude, s.latitude],
					s.id,
					siteColorForIndex(idx),
					radiiToUse,
				),
			)
			for (let i = 0; i < ringsBySite.length; i++) {
				for (let j = i + 1; j < ringsBySite.length; j++) {
					const overlaps = tradeAreaService.calculateOverlap(
						ringsBySite[i],
						ringsBySite[j],
					)
					for (const ov of overlaps) {
						overlapFeatures.push({
							...ov.polygon,
							properties: {
								...(ov.polygon.properties || {}),
								radiusMiles: ov.radiusMiles,
								siteIdA: ov.siteIds[0],
								siteIdB: ov.siteIds[1],
								overlapAreaSqMiles: ov.overlapAreaSqMiles,
							},
						})
					}
				}
			}
		}

		const collection: FeatureCollection<Polygon> = {
			type: 'FeatureCollection',
			features: overlapFeatures,
		}
		const existingSource = map.getSource(SOURCE_ID) as
			| maplibregl.GeoJSONSource
			| undefined

		if (!initialized.current || !existingSource) {
			if (map.getLayer(LAYER_ID_STROKE)) map.removeLayer(LAYER_ID_STROKE)
			if (map.getLayer(LAYER_ID_FILL)) map.removeLayer(LAYER_ID_FILL)
			if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)

			map.addSource(SOURCE_ID, { type: 'geojson', data: collection })

			map.addLayer({
				id: LAYER_ID_FILL,
				type: 'fill',
				source: SOURCE_ID,
				paint: {
					'fill-color': fillColor,
					'fill-opacity': DEFAULT_FILL_OPACITY,
				},
			})
			map.addLayer({
				id: LAYER_ID_STROKE,
				type: 'line',
				source: SOURCE_ID,
				paint: {
					'line-color': fillColor,
					'line-opacity': DEFAULT_STROKE_OPACITY,
					'line-width': DEFAULT_STROKE_WIDTH,
					'line-dasharray': [2, 2],
				},
			})
			initialized.current = true
		} else {
			existingSource.setData(collection)
		}
	}, [map, isMapLoaded, sites, visible, radii, fillColor])

	// Cleanup on unmount.
	useEffect(() => {
		return () => {
			if (!map) return
			try {
				if (map.getLayer(LAYER_ID_STROKE)) map.removeLayer(LAYER_ID_STROKE)
				if (map.getLayer(LAYER_ID_FILL)) map.removeLayer(LAYER_ID_FILL)
				if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
				initialized.current = false
			} catch {
				// map may already be torn down
			}
		}
		// We deliberately want this cleanup tied to the lifetime of the
		// component instance, not to map changes — the data-driven effect
		// above handles map/visibility transitions.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return null
}

export default OverlapZones
